import { Box, Flex, Skeleton } from '@mantine/core';
import {
  type ColumnDef,
  type ColumnOrderState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reconcileColumnOrder } from 'shared/lib/preferences';
import {
  ColumnSettings,
  Footer,
  HeaderCell,
  HeaderFilterCell,
  Table,
  Td,
  Th,
  Tr,
} from './components';
import { getGridTemplateColumns } from './helpers';
import { useTablePreferences } from './model';

export type Pagination = {
  pageIndex: number;
  pageSize: number;
};

export type Table2PaginationProps = {
  pagination: Pagination;
  setPagination: (pagination: Pagination) => void;
  total: number;
};

export type Table2FilterProps = {
  columnFilters: any;
  setColumnFilters: (updater: any) => void;
};

export type Table2SortingProps = {
  sorting: SortingState;
  setSorting: (updater: any) => void;
};

export type Table2DataState = {
  EmptyComponent?: React.ReactNode;
  NotFoundComponent?: React.ReactNode;
};

/**
 * Table2Props type
 */
export type Table2Props<TData> = Table2PaginationProps &
  Table2FilterProps &
  Table2SortingProps &
  Table2DataState & {
    /**
     * Logical identifier of this table — used as the preference key suffix
     * (persisted blob lives under `tables.${id}` in `user_preferences`).
     * Keep it short, kebab-free (e.g. `documents`, `contacts`, `packages`).
     */
    id: string;
    data: TData[];
    /**
     * TanStack column definitions.
     *
     * **Important**: every `column.id` MUST be camelCase. The shared axios
     * interceptor runs `camelizeKeys` recursively on every API response and
     * `decamelizeKeys` on every request body — including our opaque jsonb
     * preference payload. A column id like `updated_at` survives the PATCH
     * unchanged but comes back from GET as `updatedAt`, leaving the user
     * layer keyed differently than the TanStack lookup. Symptoms: column
     * visibility/order/sizing changes for that column "snap back" after
     * the API response. `useTablePreferences` logs a dev-only warning
     * when a non-camelCase id is detected.
     */
    columns: Array<
      ColumnDef<TData, any> & {
        id?: string;
        meta?: {
          disableManagement?: boolean;
          isVisible?: boolean;
        };
      }
    >;
    settingsColumn?: boolean;
    isLoading?: boolean;
    rowSelection?: TData[];
    onRowSelectionChange?: (selected: TData[]) => void;
    columnFilters?: any;
    setColumnFilters?: (updater: any) => void;
    getRowId?: (row: TData) => string;
    onClickRow?: (row: TData) => void;
    /**
     * Board id of the currently-active board. When provided, table
     * preferences (column visibility / order / sizing) are persisted to the
     * backend with three-layer precedence (code default → board default →
     * user override). When omitted, the table falls back to legacy
     * localStorage persistence.
     */
    boardId?: number;
    /**
     * Whether the current user can edit the board-wide default for this
     * table. Only relevant when `boardId` is set. Renders the
     * `Set as board default` / `Reset board default` menu items.
     */
    showAdminActions?: boolean;
  };

/**
 * Table2 component
 * This component is a wrapper around the Mantine Table component and uses useReactTable to control state.
 */
export const Table2 = <TData,>({
  id,
  data,
  columns,
  isLoading,
  rowSelection = [],
  onRowSelectionChange = () => null,
  getRowId = (row: any) => row.id,
  columnFilters,
  setColumnFilters,
  onClickRow,
  pagination,
  setPagination,
  total,
  sorting,
  setSorting,
  EmptyComponent,
  NotFoundComponent,
  settingsColumn,
  boardId,
  showAdminActions,
}: Table2Props<TData>) => {
  const { t } = useTranslation(['common']);

  const {
    state: {
      visibility: columnVisibility,
      order: storedColumnOrder,
      sizing: columnSizing,
    },
    setVisibility: setColumnVisibility,
    setOrder: setStoredColumnOrder,
    setSizing: setColumnSizing,
    preference: tablePreference,
  } = useTablePreferences(id, columns, { boardId });

  // Reconcile the persisted order against the live columns definition.
  // Fixed (`meta.disableManagement === true`) columns keep their source
  // position — they must never be displaced by a stored order that didn't
  // include them (e.g. a row-selection checkbox). New manageable columns
  // added since the user persisted their order get appended in the
  // manageable section.
  const columnIdSpecs = useMemo(
    () =>
      columns
        .map(c => c.id)
        .filter((id): id is string => Boolean(id))
        .map(id => {
          const def = columns.find(c => c.id === id);
          return { id, isFixed: def?.meta?.disableManagement === true };
        }),
    [columns],
  );
  const columnOrder = useMemo(
    () => reconcileColumnOrder(storedColumnOrder, columnIdSpecs),
    [columnIdSpecs, storedColumnOrder],
  );

  // Persistence layer should only know about manageable columns. When the
  // table writes a new order back, strip out fixed ids so a future schema
  // change (e.g. a fixed column gets renamed) doesn't leave stale entries
  // in the user / board preference blob.
  const manageableIds = useMemo(
    () => new Set(columnIdSpecs.filter(s => !s.isFixed).map(s => s.id)),
    [columnIdSpecs],
  );
  const setColumnOrder = (
    updater: ColumnOrderState | ((prev: ColumnOrderState) => ColumnOrderState),
  ) => {
    setStoredColumnOrder(prev => {
      const next =
        typeof updater === 'function'
          ? (updater as (p: ColumnOrderState) => ColumnOrderState)(prev)
          : updater;
      return next.filter(colId => manageableIds.has(colId));
    });
  };

  const [idxSelection, onIdxSelectionChange] = useState<RowSelectionState>({
    ...(rowSelection || []).reduce((acc, item) => {
      acc[getRowId(item)] = true;
      return acc;
    }, {} as RowSelectionState),
  });

  const [cacheData, setCacheData] = useState<Record<string, TData>>({});

  useEffect(() => {
    const newIds = rowSelection.map(getRowId);

    if (JSON.stringify(newIds) !== JSON.stringify(Object.keys(idxSelection))) {
      const newSelection: RowSelectionState = {};
      newIds.forEach(idSelection => {
        newSelection[idSelection] = true;
      });
      onIdxSelectionChange(newSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  useEffect(() => {
    setCacheData(s => {
      const dataDictionary = data.reduce<Record<string, TData>>((acc, item) => {
        acc[getRowId(item)] = item;
        return acc;
      }, {});

      return {
        ...s,
        ...dataDictionary,
      };
    });
  }, [data, getRowId]);

  useEffect(() => {
    onRowSelectionChange(
      Object.entries(idxSelection)
        .filter(([, value]) => value)
        .map(([key]) => cacheData[key]),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idxSelection]);

  const hasColumnFilters =
    Array.isArray(columnFilters) &&
    columnFilters.length > 0 &&
    columnFilters.some(
      (f: any) =>
        (Array.isArray(f?.value) && f.value.length > 0) ||
        (f?.value && f.value !== ''),
    );

  const table = useReactTable<TData>({
    data,
    columns,
    defaultColumn: {
      // @ts-expect-error
      size: 'auto',
    },
    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      columnSizing,
      rowSelection: idxSelection,
      pagination,
      sorting,
    },
    onColumnSizingChange: updater => {
      setColumnSizing(updater as any);
    },
    onSortingChange: newSorting => {
      setSorting?.(newSorting);
    },
    onColumnOrderChange: newOrder => {
      setColumnOrder(newOrder);
    },
    onColumnVisibilityChange: updater => {
      setColumnVisibility(updater);
    },
    onRowSelectionChange: onIdxSelectionChange,
    onColumnFiltersChange: updater => {
      setColumnFilters?.(updater);
    },
    enableMultiRowSelection: true,
    autoResetSelectedRows: false,
    // columnResizeMode: 'onChange',
    rowCount: total,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // @ts-expect-error
    onPaginationChange: setPagination,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId,
    columnResizeMode: 'onEnd',
  });

  return (
    <Table table={table}>
      <Box className="header" pos="relative">
        {settingsColumn && (
          <Flex direction="row" justify="flex-end" h={0}>
            <Box
              mt={14}
              mr={16}
              sx={{
                zIndex: 100,
                position: 'sticky',
                top: 0,
                right: 0,
              }}
            >
              <ColumnSettings
                table={table}
                setColumnOrder={(newOrder: ColumnOrderState) => {
                  // newOrder contains manageable columns only (the
                  // ColumnSettings UI hides fixed ones). reconcileColumnOrder
                  // in state.columnOrder will reinsert fixed columns on
                  // their source positions, so we don't need to prefix
                  // them here.
                  table.setColumnOrder(newOrder as any);
                }}
                preferenceKey={id}
                boardId={boardId}
                preference={tablePreference}
                showAdminActions={showAdminActions ?? false}
              />
            </Box>
          </Flex>
        )}

        {table.getHeaderGroups().map(headerGroup => (
          <Fragment key={`header-${headerGroup.id}`}>
            <Tr
              gridTemplateColumns={getGridTemplateColumns({
                headerGroup,
              })}
            >
              {headerGroup.headers.map(header => (
                <Th key={header.id} table={table} header={header}>
                  <HeaderCell header={header} />
                </Th>
              ))}
            </Tr>
            {headerGroup.headers.some(header =>
              header.column.getCanFilter(),
            ) && (
              <Tr
                key={`filter-${headerGroup.id}`}
                gridTemplateColumns={getGridTemplateColumns({
                  headerGroup,
                })}
              >
                {headerGroup.headers.map(header => (
                  <Th key={header.id} table={table} header={header}>
                    <HeaderFilterCell header={header} />
                  </Th>
                ))}
              </Tr>
            )}
          </Fragment>
        ))}
      </Box>
      <Box>
        {isLoading &&
          new Array(pagination.pageSize).fill(1).map(() =>
            table.getHeaderGroups().map(headerGroup => (
              <Fragment key={`header-${headerGroup.id}`}>
                <Tr
                  gridTemplateColumns={getGridTemplateColumns({
                    headerGroup,
                  })}
                >
                  {headerGroup.headers.map(header => {
                    const CellSkeleton =
                      header?.column?.columnDef?.meta?.skeleton;

                    return (
                      <Td key={header.id} table={table}>
                        {CellSkeleton ? (
                          <CellSkeleton />
                        ) : (
                          <Box h={24}>
                            <Skeleton height={16} width="50%" />
                          </Box>
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              </Fragment>
            )),
          )}

        {!isLoading && data.length === 0 && hasColumnFilters && (
          <Box>
            <Box>
              <Flex mih="320px" justify="center" align="center">
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {NotFoundComponent ?? t('common:notFoundResults')}
                </div>
              </Flex>
            </Box>
          </Box>
        )}

        {!isLoading && data.length === 0 && !hasColumnFilters && (
          <Box>
            <Box>
              <Flex mih="320px" justify="center" align="center">
                <div style={{ width: '100%', textAlign: 'center' }}>
                  {EmptyComponent ?? t('common:emptyCollection')}
                </div>
              </Flex>
            </Box>
          </Box>
        )}

        {table.getRowModel().rows.map(row => (
          <Tr
            className={onClickRow ? 'rowClick' : undefined}
            onClick={() => onClickRow?.(row.original)}
            key={`row-${row.id}`}
            gridTemplateColumns={getGridTemplateColumns({ row })}
          >
            {row.getVisibleCells().map(cell => (
              <Td key={cell.id} cell={cell} table={table}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Td>
            ))}
          </Tr>
        ))}
      </Box>
      <Footer table={table} />
    </Table>
  );
};
